import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { _, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { IEvent } from '@app/api/interfaces/event.interface';
import { IPosition } from '@app/core/interfaces/position.interface';
import { MeasurementSystem } from '@app/core/enums/measurement-system.enum';
import { FormattingDataService } from '@app/core/services/formatting-data/formatting-data.service';
import { selectAllEvents } from '@app/main/main-screen/store/data/events/events.selectors';
import { getDistanceMeters } from '@app/main/main-content/map/utils/geometry';
import { LdEventStatusIconComponent } from '@app/main/leak-detection/components/ld-helper/ld-event-status-icon/ld-event-status-icon.component';
import { DialogEventService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event.service';
import {
  LdActionPlace,
  LdActionType,
  LdCoordinatorDispatch,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';

// todo: update this after events implementation
/** how far around the item the `Event Details` submenu looks, and how many events it lists */
const EVENT_RADIUS_METERS = 250;
const EVENT_MAX_COUNT = 5;

export interface INearbyEvent {
  event: IEvent;
  distance: number;
}

export interface ILdEventSubmenuConfig {
  event: IEvent;
  config: MenuItem;
}

/**
 * Descriptions of the event-related context-menu entries, shared by every leak-detection context
 * menu. Each `get…Config` returns the entry without its behaviour — the menu that uses it adds its
 * own `command` (or `items`), so the same entry can mean a different action per entity.
 */
@Injectable({ providedIn: 'root' })
export class LdContextMenuEventsService {
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly formattingData = inject(FormattingDataService);
  private readonly dialogEventService = inject(DialogEventService);

  private readonly events = this.store.selectSignal(selectAllEvents);

  public getCreateEventConfig(): MenuItem {
    return {
      label: this.translateService.instant(_(`@@shared-create-event__text`)),
      icon: 'icons/icon-create-event.svg',
    };
  }

  /**
   * The `Create event` entry as it goes into the menu: the same for every entity, only the position
   * differs. The popup opens there, and the event it creates then opens as a double-click on it would.
   */
  public getCreateEventMenuItem(position: IPosition, place: LdActionPlace, dispatch: LdCoordinatorDispatch): MenuItem {
    return {
      ...this.getCreateEventConfig(),
      command: () =>
        this.dialogEventService
          .openCreate(position)
          .subscribe(item => dispatch({ type: LdActionType.EventDbClick, place, item })),
    };
  }

  /** actionable while at least one event exists to add to — the same rule for every entity */
  public getAddToExistingEventConfig(): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-add-to-existing-event__label`)),
      icon: 'icons/icon-add-to-event.svg',
      disabled: !this.events().length,
      iconStyle: { 'original-icon': !!this.events().length },
    };
  }

  /** a group — the call site adds `items` (see `getEventSubmenuConfigs`) instead of a `command` */
  public getEventDetailsConfig(nearbyEvents: INearbyEvent[]): MenuItem {
    return {
      label: this.translateService.instant(_(`@@leak-detection-context-menu-event-details__label`)),
      icon: 'icons/icon-event-details.svg',
      disabled: !nearbyEvents.length,
    };
  }

  /**
   * The `Event details` entry as it goes into the menu: a single nearby event shows directly as
   * an actionable item (no need to open a submenu for one result), several show as a group whose
   * submenu lists each one, and none show as the disabled group.
   */
  public getEventDetailsMenuItem(
    nearbyEvents: INearbyEvent[],
    viewMeasurementSystem: MeasurementSystem,
    onSelect: (event: IEvent) => void
  ): MenuItem {
    const submenuConfigs = this.getEventSubmenuConfigs(nearbyEvents, viewMeasurementSystem);

    if (submenuConfigs.length === 1) {
      const [{ event, config }] = submenuConfigs;
      return { ...config, command: () => onSelect(event) };
    }

    return {
      ...this.getEventDetailsConfig(nearbyEvents),
      items: submenuConfigs.length
        ? submenuConfigs.map(({ event, config }) => ({ ...config, command: () => onSelect(event) }))
        : undefined,
    };
  }

  /**
   * The `Event details` submenu entries — one per nearby event, labelled with its distance. Each
   * comes paired with its event, so the call site maps them into items with its own `command`.
   */
  public getEventSubmenuConfigs(
    nearbyEvents: INearbyEvent[],
    viewMeasurementSystem: MeasurementSystem
  ): ILdEventSubmenuConfig[] {
    return nearbyEvents.map(({ event, distance }) => ({
      event,
      config: {
        label: this.translateService.instant(_(`@@leak-detection-context-menu-event-item__label`), {
          I18N_VAR_EVENT_NO: event.serialNo,
          I18N_VAR_DISTANCE: this.formattingData.formatLength(distance, viewMeasurementSystem).trim(),
        }),
        // rendered by the context-menu host through `ngComponentOutlet`, so the entry looks exactly
        // like the event status in the table
        iconComponent: LdEventStatusIconComponent,
        iconInputs: { event, size: '1.25rem', styleClass: '!border-[1.5px]' },
      },
    }));
  }

  /** nearest first */
  public getNearbyEvents(position: IPosition | undefined): INearbyEvent[] {
    if (!position) return [];

    return this.events()
      .map(event => ({ event, distance: getDistanceMeters(position, event.coordinates) }))
      .filter(({ distance }) => distance <= EVENT_RADIUS_METERS)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, EVENT_MAX_COUNT);
  }
}
